import { Trans } from "@lingui/macro";

import { tools as allTools } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { canPurchaseTool, toolLockReason, toolRequirement } from "../../engine/tools";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import { formatMoney } from "../common/format";
import { MiniEffectDiagram } from "./MiniEffectDiagram";
import { ToolManifest } from "./ToolManifest";
import { ToolLockChip, ToolReqMet } from "./ToolRequirement";
import styles from "./ToolDetails.module.css";

export function ToolDetails(): JSX.Element {
  const t = useLocalize();
  const activeToolId = useGameStore((state) => state.activeToolId);
  const player = useGameStore((state) => state.player);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const setActiveTool = useGameStore((state) => state.setActiveTool);
  const purchaseTool = useGameStore((state) => state.purchaseTool);

  const tool = activeToolId ? allTools.find((entry) => entry.id === activeToolId) : null;

  if (!tool) {
    return (
      <Panel title={<Trans>Инструмент</Trans>}>
        <EmptyState
          title={<Trans>Инструмент не выбран</Trans>}
          action={
            <Button variant="ghost" size="small" onClick={() => { setActiveTool(null); navigateTo("tools"); }}>
              <Trans>Назад</Trans>
            </Button>
          }
        />
      </Panel>
    );
  }

  const owned = player.ownedTools.includes(tool.id);
  const affordable = canPurchaseTool(player, tool);
  const lockReason = toolLockReason(tool, player);
  const requirement = toolRequirement(tool);

  return (
    <Panel title={t(tool.name)}>
      <div className={styles.back}>
        <Button variant="ghost" size="small" onClick={() => { setActiveTool(null); navigateTo("tools"); }}>
          <Trans>← К инструментам</Trans>
        </Button>
      </div>
      <div className={cx(styles.body, lockReason && styles.locked)}>
        <div className={styles.meta}>
          <ToolManifest category={tool.category} />
          <span className={styles.cost}>{formatMoney(tool.cost)}</span>
        </div>
        <Card>
          <p className={styles.description}>{t(tool.description)}</p>
        </Card>
        <div className={styles.effectBlock}>
          <span className={styles.effectLabel}>
            <Trans>Что даёт</Trans>
          </span>
          <p className={styles.effect}>{t(tool.effect)}</p>
          <MiniEffectDiagram category={tool.category} />
        </div>
        {!lockReason && requirement && <ToolReqMet requirement={requirement} />}
        <div className={styles.cta}>
          {owned ? (
            <span className={styles.ownedBadge}>
              <Trans>В арсенале</Trans>
            </span>
          ) : tool.status === "upcoming" ? (
            <span className={styles.upcomingBadge}>
              <Trans>Скоро</Trans>
              {tool.upcomingType ? ` · ${t(tool.upcomingType)}` : null}
            </span>
          ) : lockReason ? (
            <ToolLockChip requirement={lockReason} />
          ) : (
            <Button
              variant="primary"
              size="small"
              disabled={!affordable}
              onClick={() => purchaseTool(tool.id)}
            >
              <Trans>Купить</Trans>
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
}
